import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { formatDate, formatDateTime } from "../../lib/datetime";
import { toast } from "sonner";
import { Trash2, Plus, X } from "lucide-react";
import Pagination, { PAGE_SIZE } from "../../components/Pagination";
import { useConfirm } from "../../components/ConfirmDialog";
import { useCompany } from "../../context/CompanyContext";
import { useAuth } from "../../context/AuthContext";

const STATUSES = ["new", "contacted", "qualified", "closed"];
const STATUS_LABEL = {
  new: "Nuevo",
  contacted: "Contactado",
  qualified: "Calificado",
  closed: "Cerrado",
};

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(null);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const confirm = useConfirm();
  const { companies } = useCompany();
  const { isAdmin } = useAuth();

  const load = () => {
    const params = filter ? { status: filter } : {};
    api.get("/admin/leads", { params }).then((r) => setLeads(r.data));
  };

  useEffect(() => {
    setPage(1);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const visible = leads.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/leads/${id}`, { status });
    toast.success("Estado actualizado");
    load();
    if (selected?.id === id) setSelected({ ...selected, status });
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: "¿Eliminar lead?",
      message: "Esta acción es permanente. ¿Continuar?",
      confirmLabel: "Eliminar",
      variant: "danger",
    });
    if (!ok) return;
    await api.delete(`/admin/leads/${id}`);
    toast.success("Lead eliminado");
    setSelected(null);
    setSelectedIds(new Set());
    load();
  };

  const toggleId = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const visibleIds = visible.map((l) => l.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const toggleAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };
  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    const ok = await confirm({
      title: `¿Eliminar ${ids.length} lead${ids.length > 1 ? "s" : ""}?`,
      message: "Esta acción es permanente. ¿Continuar?",
      confirmLabel: "Eliminar",
      variant: "danger",
    });
    if (!ok) return;
    const { data } = await api.post("/admin/leads/bulk-delete", { ids });
    toast.success(`${data.deleted} eliminado${data.deleted !== 1 ? "s" : ""}`);
    setSelectedIds(new Set());
    load();
  };

  const saveNew = async () => {
    if (!creating?.name?.trim() || !creating?.email?.trim()) {
      toast.error("Nombre y email son obligatorios");
      return;
    }
    try {
      const payload = {
        name: creating.name.trim(),
        email: creating.email.trim(),
        phone: creating.phone?.trim() || null,
        company: creating.company?.trim() || null,
        industry: creating.industry?.trim() || null,
        message: creating.message?.trim() || null,
        source: "manual",
        status: "new",
        company_id: creating.company_id || null,
      };
      await api.post("/admin/leads", payload);
      toast.success("Lead creado");
      setCreating(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo crear el lead");
    }
  };

  return (
    <div data-testid="admin-leads-page" className="p-8 md:p-10">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-brand font-mono mb-2">
            {isAdmin ? "Todos los leads" : "Mis leads"}
          </div>
          <h1 className="font-display font-bold text-3xl md:text-4xl text-ash-100">Leads</h1>
        </div>
        <button
          data-testid="lead-create"
          onClick={() =>
            setCreating({
              name: "",
              email: "",
              phone: "",
              company: "",
              industry: "",
              message: "",
              company_id: companies[0]?.id || "",
            })
          }
          className="inline-flex items-center gap-2 bg-brand text-ink-800 font-semibold hover:bg-brand-hover px-5 py-2.5 uppercase tracking-widest text-xs"
        >
          <Plus size={14} /> Nuevo lead
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-6">
        {selectedIds.size > 0 && (
          <div data-testid="bulk-bar" className="flex items-center gap-2 mr-2 px-3 py-1.5 border border-brand bg-brand/10 text-xs">
            <span className="text-brand font-mono">{selectedIds.size} seleccionado{selectedIds.size !== 1 ? "s" : ""}</span>
            <button
              data-testid="bulk-delete-btn"
              onClick={bulkDelete}
              className="inline-flex items-center gap-1.5 bg-[#9B1C1C] hover:bg-[#7e1717] text-white px-3 py-1.5 uppercase tracking-widest font-semibold"
            >
              <Trash2 size={12} /> Eliminar
            </button>
            <button onClick={() => setSelectedIds(new Set())} className="text-ash-300 hover:text-ash-100 ml-1">
              Cancelar
            </button>
          </div>
        )}
        <button
          data-testid="leads-filter-all"
          onClick={() => setFilter("")}
          className={`px-4 py-2 border text-xs uppercase tracking-widest ${filter === "" ? "bg-brand text-ink-800 border-brand" : "border-ink-500 text-ash-300 hover:border-brand"}`}
        >
          Todos ({leads.length})
        </button>
        {STATUSES.map((s) => (
          <button
            key={s}
            data-testid={`leads-filter-${s}`}
            onClick={() => setFilter(s)}
            className={`px-4 py-2 border text-xs uppercase tracking-widest ${filter === s ? "bg-brand text-ink-800 border-brand" : "border-ink-500 text-ash-300 hover:border-brand"}`}
          >
            {STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      <div className="border border-ink-500 bg-white overflow-x-auto">
        <table data-testid="leads-table" className="w-full text-sm">
          <thead>
            <tr className="bg-brand-slate text-left">
              <th className="px-3 py-3 w-10">
                <input
                  type="checkbox"
                  data-testid="leads-select-all"
                  checked={allVisibleSelected}
                  onChange={toggleAllVisible}
                  className="accent-brand cursor-pointer"
                />
              </th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-white font-mono">Nombre</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-white font-mono">Email</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-white font-mono">Empresa</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-white font-mono">Industria</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-white font-mono">Estado</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-white font-mono">Fecha</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {visible.map((l) => (
              <tr key={l.id} data-testid={`lead-row-${l.id}`} className={`border-t border-gray-200 hover:bg-gray-50 cursor-pointer ${selectedIds.has(l.id) ? "bg-brand/5" : ""}`} onClick={() => setSelected(l)}>
                <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    data-testid={`lead-select-${l.id}`}
                    checked={selectedIds.has(l.id)}
                    onChange={() => toggleId(l.id)}
                    className="accent-brand cursor-pointer"
                  />
                </td>
                <td className="px-4 py-3 text-ink-800 font-medium">{l.name}</td>
                <td className="px-4 py-3 text-ink-800">{l.email}</td>
                <td className="px-4 py-3 text-ink-800">{l.company || "—"}</td>
                <td className="px-4 py-3 text-ink-800">{l.industry || "—"}</td>
                <td className="px-4 py-3"><span className="text-xs uppercase tracking-widest text-brand font-mono">{STATUS_LABEL[l.status] || l.status}</span></td>
                <td className="px-4 py-3 text-ink-800 font-mono text-xs">{formatDate(l.created_at)}</td>
                <td className="px-4 py-3 text-right">
                  <button data-testid={`lead-delete-${l.id}`} onClick={(e) => { e.stopPropagation(); remove(l.id); }} className="text-ash-500 hover:text-destructive">
                    <Trash2 size={16} strokeWidth={1.5} />
                  </button>
                </td>
              </tr>
            ))}
            {leads.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-brand-slate">No hay leads.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} total={leads.length} onChange={setPage} />

      {selected && (
        <div className="fixed inset-0 z-50 bg-ink-900/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-ink-800 border border-ink-500 max-w-2xl w-full p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="text-xs uppercase tracking-widest text-brand font-mono mb-2">Lead</div>
                <h2 className="font-display font-bold text-2xl text-ash-100">{selected.name}</h2>
                <div className="text-ash-300 text-sm mt-1">{selected.email}</div>
              </div>
              <button onClick={() => setSelected(null)} className="text-ash-300 hover:text-ash-100">✕</button>
            </div>
            <dl className="space-y-3 text-sm border-t border-ink-500 pt-6">
              <Row label="Teléfono">{selected.phone || "—"}</Row>
              <Row label="Empresa">{selected.company || "—"}</Row>
              <Row label="Industria">{selected.industry || "—"}</Row>
              <Row label="Fuente">{selected.source}</Row>
              <Row label="Fecha">{formatDateTime(selected.created_at)}</Row>
              <Row label="Mensaje"><span className="text-ash-100">{selected.message || "—"}</span></Row>
            </dl>
            <div className="border-t border-ink-500 mt-6 pt-6 flex flex-wrap items-center gap-3">
              <span className="text-xs uppercase tracking-widest text-ash-300 mr-2">Estado:</span>
              {STATUSES.map((s) => (
                <button
                  key={s}
                  data-testid={`lead-status-${s}`}
                  onClick={() => updateStatus(selected.id, s)}
                  className={`px-4 py-3 border text-xs uppercase tracking-widest transition-colors ${selected.status === s ? "bg-brand text-ink-800 border-brand" : "border-ink-500 text-ash-100 hover:border-brand"}`}
                >
                  {STATUS_LABEL[s]}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {creating && (
        <div className="fixed inset-0 z-50 bg-ink-900/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div data-testid="lead-create-modal" className="bg-ink-800 border border-ink-500 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-ink-500">
              <h2 className="font-display font-bold text-lg text-ash-100">Crear nuevo lead</h2>
              <button onClick={() => setCreating(null)} className="text-ash-500 hover:text-ash-100"><X size={18} /></button>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Nombre *">
                <input data-testid="lead-create-name" className={createInputCls} value={creating.name} onChange={(e) => setCreating({ ...creating, name: e.target.value })} />
              </FormField>
              <FormField label="Email *">
                <input data-testid="lead-create-email" type="email" className={createInputCls} value={creating.email} onChange={(e) => setCreating({ ...creating, email: e.target.value })} />
              </FormField>
              <FormField label="Teléfono">
                <input data-testid="lead-create-phone" className={createInputCls} value={creating.phone} onChange={(e) => setCreating({ ...creating, phone: e.target.value })} />
              </FormField>
              <FormField label="Empresa">
                <input data-testid="lead-create-company" className={createInputCls} value={creating.company} onChange={(e) => setCreating({ ...creating, company: e.target.value })} />
              </FormField>
              <FormField label="Industria">
                <input data-testid="lead-create-industry" className={createInputCls} value={creating.industry} onChange={(e) => setCreating({ ...creating, industry: e.target.value })} />
              </FormField>
              <FormField label="Asignar a empresa">
                <select data-testid="lead-create-company-id" className={createInputCls} value={creating.company_id || ""} onChange={(e) => setCreating({ ...creating, company_id: e.target.value })}>
                  <option value="">— Sin asignar —</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </FormField>
              <div className="md:col-span-2">
                <FormField label="Mensaje / Notas">
                  <textarea data-testid="lead-create-message" rows={4} className={createInputCls} value={creating.message} onChange={(e) => setCreating({ ...creating, message: e.target.value })} />
                </FormField>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-ink-500 flex justify-end gap-3">
              <button onClick={() => setCreating(null)} className="px-4 py-2 border border-ink-500 text-ash-300 hover:text-ash-100 text-sm">Cancelar</button>
              <button data-testid="lead-create-save" onClick={saveNew} className="px-5 py-2 bg-brand text-ink-800 font-semibold hover:bg-brand-hover uppercase tracking-widest text-xs">
                Crear lead
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const createInputCls = "w-full bg-ink-900 border border-ink-500 text-ash-100 focus:border-brand outline-none px-3 py-2 text-sm placeholder:text-ash-500";

function FormField({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-widest text-ash-500 font-mono mb-1">{label}</span>
      {children}
    </label>
  );
}

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-3 gap-4">
      <dt className="text-ash-300 text-xs uppercase tracking-widest font-mono">{label}</dt>
      <dd className="col-span-2 text-ash-100">{children}</dd>
    </div>
  );
}
